//
// # SelfTest
//
// Self-test class for continuous animation and FPS monitoring
//

import type { Editor } from "./editor";

export class SelfTest {
  private animationId: number | null = null;

  // The radius is in page / pixel values - it will be scaled for zoom and content size.
  private static readonly ANIMATION_RADIUS: number = 100;

  // Keep a copy of the original transforms to restore later:
  private schToPage_X: number = 0.0;
  private schToPage_Y: number = 0.0;
  private schToPage_Scale: number = 1.0;

  // The schematic-space point the viewport is initially centered on:
  private initialSchCenter: { x: number; y: number } = { x: 0, y: 0 };

  private angle: number = 0.0;
  private anglePerSec: number = 0.5;
  private zoomIndex: number = 0;
  private lastTime: number = 0;

  constructor(private editor: Editor) {}

  start(): SelfTest {
    this.editor.log("[DEBUG] Starting Editor self-test");

    this.editor.log(
      `[DEBUG] Using radius: ${SelfTest.ANIMATION_RADIUS.toFixed(5)}`
    );

    // Store the components of the original schToPage transform:
    this.schToPage_X = this.editor.canvas.worldTransforms.schToPage.offset.x;
    this.schToPage_Y = this.editor.canvas.worldTransforms.schToPage.offset.y;
    this.schToPage_Scale = this.editor.canvas.worldTransforms.schToPage.scale;

    // Radius in schematic space (convert from page pixels)
    const schRadius = SelfTest.ANIMATION_RADIUS / this.schToPage_Scale;

    // Compute the schematic-space center of the current viewport:
    const screenCenterX = this.editor.canvas.size.width / 2;
    const screenCenterY = this.editor.canvas.size.height / 2;
    this.initialSchCenter = this.editor.canvas.worldTransforms.toSch({
      x: screenCenterX,
      y: screenCenterY,
    });

    // Dump everything we know about the transforms to the console:
    this.editor.log(`[DEBUG] Initial Transform - pageToSch: Offset (\
${this.editor.canvas.worldTransforms.pageToSch.offset.x.toFixed(5)}, \
${this.editor.canvas.worldTransforms.pageToSch.offset.y.toFixed(5)}), \
Scale: ${this.editor.canvas.worldTransforms.pageToSch.scale.toFixed(5)}`);
    this.editor.log(`[DEBUG] Initial Transform - schToPage: Offset (\
${this.editor.canvas.worldTransforms.schToPage.offset.x.toFixed(5)}, \
${this.editor.canvas.worldTransforms.schToPage.offset.y.toFixed(5)}), \
Scale: ${this.editor.canvas.worldTransforms.schToPage.scale.toFixed(5)}`);

    // Dump viewport info:
    const canvas = this.editor.canvas;
    this.editor.log(
      `[DEBUG] Viewport (page): 0,0 to ${canvas.size.width},${canvas.size.height}`
    );

    // Convert viewport corners from page to schematic coords
    const topLeft = this.editor.canvas.worldTransforms.toSch({ x: 0, y: 0 });
    const bottomRight = this.editor.canvas.worldTransforms.toSch({
      x: canvas.size.width,
      y: canvas.size.height,
    });
    this.editor.log(
      `[DEBUG] Viewport (sch): (${topLeft.x.toFixed(2)}, ${topLeft.y.toFixed(
        2
      )}) to (${bottomRight.x.toFixed(2)}, ${bottomRight.y.toFixed(2)})`
    );

    this.angle = 0.0;
    this.zoomIndex = 0;

    // Animation loop
    const animate = () => {
      // Note: we used to calculate frame rate and timings here,
      // but I've moved them to the editor so we don't duplicate code.
      if (this.lastTime === 0) {
        this.lastTime = performance.now();
      }

      const currentTime = performance.now();
      const frameTime = currentTime - this.lastTime;
      this.lastTime = currentTime;
      const lastFrameSeconds = frameTime / 1000.0;

      // Increment the angle based on anglePerSec and frame time:
      this.angle += this.anglePerSec * lastFrameSeconds;
      this.zoomIndex += lastFrameSeconds;

      const newZoom =
        1.0 / ((Math.cos((this.zoomIndex * 6.28) / 5.0) + 1) / 2 + 0.01);

      // Target point on the circle, in schematic coords
      const targetX =
        this.initialSchCenter.x + schRadius * Math.sin(this.angle);
      const targetY =
        this.initialSchCenter.y + schRadius * Math.cos(this.angle);

      // Compute the schToPage offset that centers the target point on screen.
      // schToPage: screenX = schX * scale + offset.x
      //            screenY = -schY * scale + offset.y
      const scale = this.schToPage_Scale * newZoom;
      const screenCX = this.editor.canvas.size.width / 2;
      const screenCY = this.editor.canvas.size.height / 2;
      const newOffset = {
        x: screenCX - targetX * scale,
        y: screenCY + targetY * scale,
      };

      // Update the transforms with the new offset - this triggers a re-render
      this.editor.canvas.updateWorldTransforms(
        {
          scale,
          offset: newOffset,
        },
        "schToPage"
      );

      // Continue the animation
      this.animationId = requestAnimationFrame(animate);
    };

    // Start the animation
    window.addEventListener("keydown", this.stop, { once: true });
    this.editor.log("Self-test animation started. Press any key to stop.");
    this.animationId = requestAnimationFrame(animate);
    return this;
  }

  stop = () => {
    this.editor.log("Stopping Editor self-test");

    // Cancel animation frame if active
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Restore original transforms
    this.editor.canvas.updateWorldTransforms(
      {
        scale: this.schToPage_Scale,
        offset: { x: this.schToPage_X, y: this.schToPage_Y },
      },
      "schToPage"
    );

    // Remove the key listener
    window.removeEventListener("keydown", this.stop);

    // Clear the editor's reference to this instance
    this.editor.selfTester = null;
  };
}
